import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Input, Modal, Form, Select, Tag, Space,
  Card, message, Tooltip, Popconfirm, Badge, Drawer, Timeline, Tabs,
} from 'antd';
import {
  WifiOutlined, PlusOutlined, SearchOutlined, HomeOutlined,
  EditOutlined, DeleteOutlined, LinkOutlined, HistoryOutlined,
  DisconnectOutlined, ThunderboltOutlined, SyncOutlined, DownloadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import { isAdmin, canEdit } from '../utils/auth';
import Footer from '../components/Footer';
import CreatableSelect from '../components/CreatableSelect';

const { Option } = Select;

const MARCAS_DEFAULT = ['Teltonika', 'Queclink', 'Coban', 'Concox', 'Sinotrack', 'Suntech', 'Ruptela'];

const ESTADO_COLOR = { stock: 'warning', instalado: 'success', baja: 'error', pendiente_baja: 'orange' };
const ESTADO_LABEL = { stock: 'En stock', instalado: 'Instalado', baja: 'Baja', pendiente_baja: 'A desinstalar' };

const GPS = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalAsignar, setModalAsignar] = useState(false);
  const [editando, setEditando] = useState(null);
  const [asignando, setAsignando] = useState(null);
  const [form] = Form.useForm();
  const [formEditar] = Form.useForm();
  const [formAsignar] = Form.useForm();
  const [marcas, setMarcas] = useState(MARCAS_DEFAULT);
  const [modelos, setModelos] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [drawerHistorial, setDrawerHistorial] = useState(false);
  const [historialData, setHistorialData] = useState({ gps: null, historial: [] });
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [tabActivo, setTabActivo] = useState('gps');
  const [sensores, setSensores] = useState([]);
  const [loadingSensores, setLoadingSensores] = useState(false);
  const [drawerSensor, setDrawerSensor] = useState(false);
  const [editandoSensor, setEditandoSensor] = useState(null);
  const [formSensor] = Form.useForm();
  const [sincronizando, setSincronizando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const [gpsRes, vehRes] = await Promise.all([
        axiosInstance.get('/gps'),
        axiosInstance.get('/vehiculos'),
      ]);
      setItems(gpsRes.data);
      setVehiculos(vehRes.data);
      // Derivar listas únicas de marcas y modelos de los datos existentes
      const marcasExistentes = [...new Set(gpsRes.data.map(g => g.marca).filter(Boolean))];
      const modelosExistentes = [...new Set(gpsRes.data.map(g => g.modelo).filter(Boolean))];
      setMarcas(prev => [...new Set([...prev, ...marcasExistentes])]);
      setModelos(prev => [...new Set([...prev, ...modelosExistentes])]);
    } catch { message.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const cargarSensores = async () => {
    setLoadingSensores(true);
    try {
      const res = await axiosInstance.get('/sensores');
      setSensores(res.data);
    } catch { message.error('Error al cargar sensores'); }
    finally { setLoadingSensores(false); }
  };

  useEffect(() => { cargar(); }, []);
  useEffect(() => { if (tabActivo === 'sensores') cargarSensores(); }, [tabActivo]);

  const onGuardarSensor = async (values) => {
    try {
      if (editandoSensor) {
        await axiosInstance.put(`/sensores/${editandoSensor.id}`, values);
        message.success('Sensor actualizado');
      } else {
        await axiosInstance.post('/sensores', values);
        message.success('Sensor registrado');
      }
      setDrawerSensor(false);
      formSensor.resetFields();
      setEditandoSensor(null);
      cargarSensores();
    } catch (e) { message.error(e.response?.data?.error || 'Error'); }
  };

  const onEliminarSensor = async (id) => {
    try {
      await axiosInstance.delete(`/sensores/${id}`);
      message.success('Sensor eliminado');
      cargarSensores();
    } catch { message.error('Error al eliminar sensor'); }
  };

  const abrirDrawerSensor = (sensor = null) => {
    setEditandoSensor(sensor);
    formSensor.resetFields();
    if (sensor) formSensor.setFieldsValue(sensor);
    setDrawerSensor(true);
  };

  const TIPO_SENSOR_COLOR = {
    temperatura: '#e74c3c', puerta: '#3498db', combustible: '#f39c12',
    rpm: '#9b59b6', voltaje: '#27ae60', otro: '#95a5a6',
  };

  const columnasSensores = [
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo', render: v => <Tag color={TIPO_SENSOR_COLOR[v] || '#95a5a6'} style={{ textTransform: 'capitalize' }}>{v}</Tag> },
    { title: 'Marca / Modelo', key: 'marca_modelo', render: (_, r) => <span>{r.marca || '—'}{r.modelo ? ` ${r.modelo}` : ''}</span> },
    { title: 'Conectividad', dataIndex: 'conectividad', key: 'conectividad', render: v => v || '—' },
    { title: 'Nº de serie', dataIndex: 'numero_serie', key: 'numero_serie', render: v => v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : '—' },
    { title: 'GPS (IMEI)', dataIndex: 'gps_imei', key: 'gps_imei', render: v => v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : '—' },
    { title: 'Vehículo', dataIndex: 'vehiculo_chapa', key: 'vehiculo_chapa', render: (v, r) => v ? <span>{v}{r.vehiculo_nombre_gps ? ` — ${r.vehiculo_nombre_gps}` : ''}</span> : '—' },
    { title: 'Estado', dataIndex: 'estado', key: 'estado', render: v => <Tag color={v === 'instalado' ? 'success' : v === 'baja' ? 'error' : 'warning'}>{v === 'instalado' ? 'Instalado' : v === 'baja' ? 'Baja' : 'En stock'}</Tag> },
    { title: 'Observaciones', dataIndex: 'observaciones', key: 'observaciones', render: v => v ? <span style={{ color: '#666', fontSize: 12 }}>{v.length > 40 ? v.slice(0, 40) + '…' : v}</span> : '—' },
    ...(canEdit() ? [{
      title: '', key: 'acc', width: 80,
      render: (_, r) => (
        <Space size={0}>
          <Tooltip title="Editar"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => abrirDrawerSensor(r)} /></Tooltip>
          <Popconfirm title="¿Eliminar sensor?" onConfirm={() => onEliminarSensor(r.id)} okText="Sí" cancelText="No" okButtonProps={{ danger: true }}>
            <Tooltip title="Eliminar"><Button type="text" size="small" icon={<DeleteOutlined />} danger /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const filtrados = items.filter(g => {
    if (!search) return true;
    const s = search.toLowerCase();
    return g.imei?.toLowerCase().includes(s) || g.marca?.toLowerCase().includes(s) ||
      g.modelo?.toLowerCase().includes(s) || g.numero_linea?.includes(s) ||
      g.vehiculo_chapa?.toLowerCase().includes(s) ||
      g.vehiculo_nombre_gps?.toLowerCase().includes(s);
  });

  const onBuscar = (val) => { setSearch(val); setPaginaActual(1); };

  const onCrear = async (values) => {
    try {
      await axiosInstance.post('/gps', values);
      message.success('GPS registrado');
      setModalNuevo(false); form.resetFields(); cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error al crear'); }
  };

  const abrirEditar = (item) => {
    setEditando(item);
    formEditar.setFieldsValue({ marca: item.marca, modelo: item.modelo, numero_linea: item.numero_linea, estado: item.estado });
    setModalEditar(true);
  };

  const onEditar = async (values) => {
    try {
      await axiosInstance.put(`/gps/${editando.id}`, values);
      message.success('GPS actualizado'); setModalEditar(false); cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error al actualizar'); }
  };

  const abrirAsignar = (item) => {
    setAsignando(item);
    formAsignar.setFieldsValue({ vehiculo_chapa: item.vehiculo_chapa || undefined });
    setModalAsignar(true);
  };

  const onAsignar = async (values) => {
    try {
      await axiosInstance.post(`/gps/${asignando.id}/asignar`, values);
      message.success(values.vehiculo_chapa ? 'GPS asignado al vehículo' : 'GPS desasignado');
      setModalAsignar(false); cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error'); }
  };

  const onDesvincular = async (id) => {
    try {
      await axiosInstance.post(`/gps/${id}/asignar`, { vehiculo_chapa: null });
      message.success('GPS desvinculado del vehículo');
      cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error al desvincular'); }
  };

  const onEliminar = async (id) => {
    try {
      await axiosInstance.delete(`/gps/${id}`);
      message.success('GPS eliminado'); cargar();
    } catch (e) { message.error(e.response?.data?.error || 'Error al eliminar'); }
  };

  const exportarExcel = () => {
    const datos = items.map(g => ({
      'IMEI':      g.imei,
      'Marca':     g.marca || '—',
      'Modelo':    g.modelo || '—',
      'Línea SIM': g.linea_numero || g.numero_linea || '—',
      'Estado':    ESTADO_LABEL[g.estado] || g.estado || '—',
      'Vehículo':  g.vehiculo_chapa || '—',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(datos), 'Dispositivos GPS');
    XLSX.writeFile(wb, `HSS_Modulo_GPS_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const sincronizarTrack1 = async () => {
    setSincronizando(true);
    try {
      const res = await axiosInstance.post('/gps/actualizar-desde-track1');
      const { nuevos, actualizados, total } = res.data;
      message.success(`Sincronización completa — ${nuevos} nuevos, ${actualizados} actualizados (${total} procesados)`);
      cargar();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al sincronizar con Track1');
    } finally {
      setSincronizando(false);
    }
  };

  const abrirHistorial = async (item) => {
    setDrawerHistorial(true);
    setLoadingHistorial(true);
    setHistorialData({ gps: item, historial: [] });
    try {
      const res = await axiosInstance.get(`/gps/${item.id}/historial`);
      setHistorialData(res.data);
    } catch { message.error('Error al cargar historial'); }
    finally { setLoadingHistorial(false); }
  };

  const columns = [
    {
      title: 'IMEI', dataIndex: 'imei', key: 'imei',
      sorter: (a, b) => (a.imei || '').localeCompare(b.imei || ''),
      render: v => <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{v}</span>,
    },
    {
      title: 'Marca / Modelo', key: 'dispositivo',
      sorter: (a, b) => (a.marca || '').localeCompare(b.marca || ''),
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{r.marca}</div>
          <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{r.modelo}</div>
        </div>
      ),
    },
    {
      title: 'Línea SIM', key: 'linea',
      sorter: (a, b) => (a.linea_numero || a.numero_linea || '').localeCompare(b.linea_numero || b.numero_linea || ''),
      render: (_, r) => {
        const numero = r.linea_numero || r.numero_linea;
        const iccid = r.linea_iccid;
        if (!numero && !iccid) return <span style={{ color: hssColors.gray400 }}>—</span>;
        return (
          <div>
            {numero && <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{numero}</div>}
            {iccid && <div style={{ fontFamily: 'monospace', fontSize: 11, color: hssColors.textSecondary }}>{iccid}</div>}
          </div>
        );
      },
    },
    {
      title: 'Estado', dataIndex: 'estado', key: 'estado',
      sorter: (a, b) => (a.estado || '').localeCompare(b.estado || ''),
      render: e => <Tag color={ESTADO_COLOR[e] || 'default'}>{ESTADO_LABEL[e] || e}</Tag>,
    },
    {
      title: 'Vehículo', dataIndex: 'vehiculo_chapa', key: 'vehiculo',
      sorter: (a, b) => (a.vehiculo_chapa || '').localeCompare(b.vehiculo_chapa || ''),
      render: (chapa) => chapa ? (
        <span
          style={{ color: hssColors.primary, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'monospace' }}
          onClick={() => navigate(`/vehiculos/${chapa}`)}
        >
          {chapa}
        </span>
      ) : <Tag color="orange" style={{ fontWeight: 600 }}>Sin chapa</Tag>,
    },
    {
      title: '', key: 'acciones', width: 120,
      render: (_, r) => (
        <Space>
          <Tooltip title="Historial de asignaciones">
            <Button type="text" icon={<HistoryOutlined />} style={{ color: hssColors.textSecondary }} onClick={() => abrirHistorial(r)} />
          </Tooltip>
          <Tooltip title={r.vehiculo_chapa ? 'Reasignar vehículo' : 'Asignar a vehículo'}>
            <Button type="text" icon={<LinkOutlined />} style={{ color: hssColors.info }} onClick={() => abrirAsignar(r)} />
          </Tooltip>
          {r.vehiculo_chapa && (
            <Popconfirm
              title="¿Desvincular este GPS del vehículo?"
              description={`Se quitará la asignación al vehículo ${r.vehiculo_chapa}`}
              onConfirm={() => onDesvincular(r.id)}
              okText="Desvincular"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
            >
              <Tooltip title="Desvincular vehículo">
                <Button type="text" icon={<DisconnectOutlined />} style={{ color: hssColors.error }} />
              </Tooltip>
            </Popconfirm>
          )}
          {canEdit() && (
            <Tooltip title="Editar">
              <Button type="text" icon={<EditOutlined />} style={{ color: hssColors.primary }} onClick={() => abrirEditar(r)} />
            </Tooltip>
          )}
          {isAdmin() && (
            <Popconfirm title="¿Eliminar este GPS?" onConfirm={() => onEliminar(r.id)} okText="Sí" cancelText="No">
              <Button type="text" icon={<DeleteOutlined />} style={{ color: hssColors.error }} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const stockCount        = items.filter(g => g.estado === 'stock').length;
  const instaladoCount    = items.filter(g => g.estado === 'instalado').length;
  const bajaCount         = items.filter(g => g.estado === 'baja').length;
  const pendienteBajaCount = items.filter(g => g.estado === 'pendiente_baja').length;
  const marcasCount    = new Set(items.map(g => g.marca).filter(Boolean)).size;
  const teltonika      = items.filter(g => g.marca === 'Teltonika').length;
  const calamp         = items.filter(g => g.marca === 'Calamp').length;

  const StatPill = ({ label, value, color }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        background: color + '18', color, border: `1px solid ${color}40`,
        borderRadius: 20, padding: '2px 12px', fontSize: 13, fontWeight: 700, minWidth: 36, textAlign: 'center',
      }}>{value}</span>
      <span style={{ fontSize: 12, color: hssColors.textSecondary }}>{label}</span>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <WifiOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Módulo GPS</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Gestión de dispositivos de rastreo satelital</p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalNuevo(true)} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Nuevo GPS
            </Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>
        <Tabs activeKey={tabActivo} onChange={setTabActivo} size="large" style={{ marginBottom: 0 }}
          tabBarExtraContent={
            tabActivo === 'gps' ? (
              <Space>
                <Button icon={<DownloadOutlined />} onClick={exportarExcel} style={{ borderRadius: 8 }}>
                  Exportar Excel ({items.length})
                </Button>
                {isAdmin() && (
                  <Button icon={<SyncOutlined />} onClick={sincronizarTrack1} loading={sincronizando} style={{ borderRadius: 8 }}>
                    Actualizar desde Track1
                  </Button>
                )}
              </Space>
            ) : tabActivo === 'sensores' && canEdit() ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => abrirDrawerSensor()} style={{ borderRadius: 8 }}>Nuevo sensor</Button>
            ) : null
          }
          items={[
            {
              key: 'gps',
              label: <Space><WifiOutlined />Dispositivos GPS</Space>,
              children: (
                <>
                  {/* Stats */}
                  <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
                    {[
                      { label: 'Total GPS', value: items.length, icon: <WifiOutlined />, color: hssColors.primary },
                      { label: 'Instalados', value: instaladoCount, icon: <WifiOutlined />, color: hssColors.success },
                      { label: 'En stock', value: stockCount, icon: <WifiOutlined />, color: hssColors.warning },
                      { label: 'Sin chapa', value: items.filter(g => !g.vehiculo_chapa).length, icon: <WifiOutlined />, color: '#e67e22' },
                    ].map(s => (
                      <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                          <div>
                            <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                            <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                    <StatPill label="Teltonika" value={teltonika} color="#0062cc" />
                    <StatPill label="Calamp" value={calamp} color="#e67e22" />
                    {marcasCount > 2 && <StatPill label="otras marcas" value={marcasCount - 2} color={hssColors.textSecondary} />}
                    {(pendienteBajaCount > 0 || bajaCount > 0) && <span style={{ color: hssColors.gray300, fontSize: 12 }}>|</span>}
                    {pendienteBajaCount > 0 && <StatPill label="a desinstalar" value={pendienteBajaCount} color="#e67e22" />}
                    {bajaCount > 0 && <StatPill label="baja" value={bajaCount} color={hssColors.error} />}
                  </div>
                  <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: 0 } }}
                    title={<Input placeholder="Buscar por IMEI, marca, modelo, línea, vehículo, sobrenombre..."
                      prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
                      style={{ width: 420, borderRadius: 8 }} size="large"
                      value={search} onChange={e => onBuscar(e.target.value)} allowClear />}
                  >
                    <Table columns={columns} dataSource={filtrados} rowKey="id" loading={loading}
                      scroll={{ x: 'max-content' }}
                      pagination={{ current: paginaActual, pageSize, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], onChange: (p, size) => { setPaginaActual(p); setPageSize(size); }, showTotal: t => `${t} dispositivos` }} />
                  </Card>
                </>
              ),
            },
            {
              key: 'sensores',
              label: <Space><ThunderboltOutlined />Sensores</Space>,
              children: (
                <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: 0 } }}>
                  <Table columns={columnasSensores} dataSource={sensores} rowKey="id" loading={loadingSensores}
                    scroll={{ x: 'max-content' }}
                    pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '20', '50'], showTotal: t => `${t} sensores` }} />
                </Card>
              ),
            },
          ]}
        />
      </div>

      {/* Modal Nuevo GPS */}
      <Modal title={<Space style={{ fontSize: 18, fontWeight: 700, color: hssColors.primary }}><WifiOutlined /><span>Nuevo dispositivo GPS</span></Space>}
        open={modalNuevo} onCancel={() => { setModalNuevo(false); form.resetFields(); }} footer={null} width={520}>
        <Form form={form} onFinish={onCrear} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="marca" label="Marca" rules={[{ required: true, message: 'Requerido' }]}>
            <CreatableSelect
              options={marcas}
              onOptionsChange={setMarcas}
              placeholder="Seleccionar o agregar marca..."
            />
          </Form.Item>
          <Form.Item name="modelo" label="Modelo" rules={[{ required: true, message: 'Requerido' }]}>
            <CreatableSelect
              options={modelos}
              onOptionsChange={setModelos}
              placeholder="Seleccionar o agregar modelo..."
            />
          </Form.Item>
          <Form.Item name="imei" label="IMEI / ID" rules={[{ required: true, message: 'Requerido' }]}>
            <Input size="large" style={{ borderRadius: 8, fontFamily: 'monospace' }} placeholder="IMEI o identificador del dispositivo" />
          </Form.Item>
          <Form.Item name="estado" label="Estado" initialValue="stock">
            <Select size="large" style={{ borderRadius: 8 }}>
              <Option value="instalado">Instalado</Option>
              <Option value="stock">En stock</Option>
              <Option value="baja">Baja</Option>
              <Option value="pendiente_baja">A desinstalar</Option>
            </Select>
          </Form.Item>
          <Form.Item name="vehiculo_chapa" label="Asignar a vehículo (opcional)">
            <Select size="large" showSearch allowClear placeholder="Buscar chapa..." optionFilterProp="label">
              {vehiculos.map(v => <Option key={v.chapa} value={v.chapa} label={`${v.chapa} — ${v.marca} ${v.modelo}`}>{v.chapa} — {v.marca} {v.modelo}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalNuevo(false)} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>Guardar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Editar GPS */}
      <Modal title={<Space style={{ fontSize: 18, fontWeight: 700, color: hssColors.primary }}><EditOutlined /><span>Editar GPS — IMEI {editando?.imei}</span></Space>}
        open={modalEditar} onCancel={() => setModalEditar(false)} footer={null} width={520}>
        <Form form={formEditar} onFinish={onEditar} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="marca" label="Marca" rules={[{ required: true }]}>
            <CreatableSelect options={marcas} onOptionsChange={setMarcas} placeholder="Marca" />
          </Form.Item>
          <Form.Item name="modelo" label="Modelo" rules={[{ required: true }]}>
            <CreatableSelect options={modelos} onOptionsChange={setModelos} placeholder="Modelo" />
          </Form.Item>
          <Form.Item name="estado" label="Estado">
            <Select size="large">
              <Option value="instalado">Instalado</Option>
              <Option value="stock">En stock</Option>
              <Option value="baja">Baja</Option>
              <Option value="pendiente_baja">A desinstalar</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalEditar(false)} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>Guardar cambios</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Asignar vehículo */}
      <Modal title={<Space style={{ fontSize: 18, fontWeight: 700, color: hssColors.info }}><LinkOutlined /><span>Asignar vehículo — {asignando?.imei}</span></Space>}
        open={modalAsignar} onCancel={() => setModalAsignar(false)} footer={null} width={480}>
        <Form form={formAsignar} onFinish={onAsignar} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="vehiculo_chapa" label="Vehículo">
            <Select size="large" showSearch allowClear placeholder="Dejar vacío para desasignar" optionFilterProp="label">
              {vehiculos.map(v => <Option key={v.chapa} value={v.chapa} label={`${v.chapa} — ${v.marca} ${v.modelo}`}>{v.chapa} — {v.marca} {v.modelo}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalAsignar(false)} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
              <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, fontWeight: 600 }}>Confirmar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Footer />

      {/* Drawer: Historial de asignaciones */}
      <Drawer
        title={
          <Space>
            <HistoryOutlined style={{ color: hssColors.primary }} />
            <span style={{ fontWeight: 700, color: hssColors.primary }}>
              Historial — {historialData.gps?.marca} {historialData.gps?.modelo} ({historialData.gps?.imei})
            </span>
          </Space>
        }
        open={drawerHistorial}
        onClose={() => setDrawerHistorial(false)}
        width={480}
      >
        {loadingHistorial ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: hssColors.textSecondary }}>Cargando...</div>
        ) : historialData.historial.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: hssColors.textSecondary }}>
            <HistoryOutlined style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
            Sin movimientos registrados
          </div>
        ) : (
          <Timeline mode="left" items={historialData.historial.map(h => {
            const color = h.evento === 'instalado' ? 'green' : h.evento === 'desinstalado' ? 'red' : 'blue';
            const label = h.evento === 'instalado' ? 'Instalado en' : h.evento === 'desinstalado' ? 'Desinstalado de' : 'Reasignado';
            return {
              color,
              label: <span style={{ fontSize: 11, color: hssColors.textSecondary }}>{h.fecha ? new Date(h.fecha).toLocaleDateString('es-PY') : '-'}</span>,
              children: (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontWeight: 600 }}>
                    <Tag color={color} style={{ fontSize: 11 }}>{h.evento.toUpperCase()}</Tag>
                    {h.evento === 'reasignado'
                      ? <span style={{ fontSize: 13 }}><span style={{ color: hssColors.error }}>{h.vehiculo_anterior || '—'}</span> → <span style={{ color: hssColors.success }}>{h.vehiculo_nuevo || '—'}</span></span>
                      : <span style={{ fontSize: 13, color: h.evento === 'instalado' ? hssColors.success : hssColors.error }}>{h.vehiculo_nuevo || h.vehiculo_anterior || '—'}</span>
                    }
                  </div>
                  {h.usuario_nombre && <div style={{ fontSize: 11, color: hssColors.textSecondary }}>{h.usuario_nombre}</div>}
                  {h.observacion && <div style={{ fontSize: 11, color: hssColors.textSecondary, fontStyle: 'italic' }}>{h.observacion}</div>}
                </div>
              ),
            };
          })} />
        )}
      </Drawer>

      {/* Drawer: Crear / Editar Sensor */}
      <Drawer
        title={
          <Space>
            <ThunderboltOutlined style={{ color: hssColors.primary }} />
            <span style={{ fontWeight: 700, color: hssColors.primary }}>
              {editandoSensor ? `Editar sensor #${editandoSensor.id}` : 'Nuevo sensor'}
            </span>
          </Space>
        }
        open={drawerSensor}
        onClose={() => { setDrawerSensor(false); setEditandoSensor(null); formSensor.resetFields(); }}
        width={480}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setDrawerSensor(false); setEditandoSensor(null); formSensor.resetFields(); }} size="large" style={{ borderRadius: 8 }}>Cancelar</Button>
            <Button type="primary" size="large" style={{ borderRadius: 8, fontWeight: 600 }} onClick={() => formSensor.submit()}>
              {editandoSensor ? 'Guardar cambios' : 'Crear sensor'}
            </Button>
          </Space>
        }
      >
        <Form form={formSensor} onFinish={onGuardarSensor} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="tipo" label="Tipo de sensor" rules={[{ required: true, message: 'Selecciona el tipo' }]}>
            <Select size="large" placeholder="Tipo">
              {['temperatura', 'puerta', 'combustible', 'rpm', 'voltaje', 'otro'].map(t => (
                <Option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="conectividad" label="Conectividad">
            <Select size="large" placeholder="Tipo de conexión" allowClear>
              {['cableado', 'RS232', 'RS485', 'BLE', 'inalámbrico'].map(c => (
                <Option key={c} value={c}>{c}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="marca" label="Marca">
            <Input size="large" placeholder="Marca del sensor" />
          </Form.Item>
          <Form.Item name="modelo" label="Modelo">
            <Input size="large" placeholder="Modelo" />
          </Form.Item>
          <Form.Item name="numero_serie" label="Número de serie (opcional)">
            <Input size="large" placeholder="N/A si no aplica" />
          </Form.Item>
          <Form.Item name="observaciones" label="Observaciones">
            <Input.TextArea rows={3} placeholder="Notas adicionales..." />
          </Form.Item>
          <Form.Item name="estado" label="Estado" initialValue="stock">
            <Select size="large">
              <Option value="stock">En stock</Option>
              <Option value="instalado">Instalado</Option>
              <Option value="baja">Baja</Option>
            </Select>
          </Form.Item>
          <Form.Item name="gps_id" label="GPS asociado">
            <Select size="large" showSearch allowClear placeholder="Seleccionar GPS (opcional)" optionFilterProp="label">
              {items.map(g => (
                <Option key={g.id} value={g.id} label={g.imei}>{g.imei} — {g.marca} {g.modelo}{g.vehiculo_chapa ? ` (${g.vehiculo_chapa})` : ''}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default GPS;
